import { useCallback, useEffect, useRef, useState } from "react";
import KeyboardArrowDownRounded from "@mui/icons-material/KeyboardArrowDownRounded";
import KeyboardArrowUpRounded from "@mui/icons-material/KeyboardArrowUpRounded";
import {
  Box,
  Button,
  ButtonBase,
  Chip,
  Divider,
  Drawer,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { formatMoney } from "@bw/shared/utils";
import { useCart, type CartSlot } from "../state/cartStore";
import { CartItemRow } from "./CartItemRow";
import { useIsNarrow } from "../hooks/useIsNarrow";
import { dateLabel } from "../lib/venueDate";

/**
 * L2 shopping-cart — the headline booking feature.
 *
 * Shows the client-side selection set, each line's per-slot ESTIMATE, and an
 * estimated subtotal EXPLICITLY labelled an estimate (the 3a price firewall —
 * there is no binding price in this slice). Remove and Clear mutate the cart
 * store. The "Review & pay" CTA is present but does NOT quote or charge: it
 * hands off to the 3b review step. There is no booking API wired in 3a.
 *
 * Two presentations, chosen by viewport (`useIsNarrow`, ≤720px = mobile):
 *
 * • DESKTOP (`DesktopCartRail`) — the static right-rail "YOUR CART" panel: the
 *   full per-day list, remove controls, total, and CTA, always visible. It never
 *   overlaps the grid (it lives in its own column), so it is unchanged.
 *
 * • MOBILE (`MobileCartBar`) — an ultra-minimal fixed bottom bar (ONE row):
 *   `● N selected · $total` (a toggle) + a `Review & pay →` CTA. The selected
 *   slots are ALREADY shown in the grid as lime ✓ pills, so the bar does NOT
 *   re-list them — the itemized list is opt-in via a bottom SHEET that expands
 *   over a scrim. This keeps the grid's last time rows reachable (the sheet
 *   page reserves bottom padding = the bar height). When the cart is empty the
 *   bar does not render (no fake empty bar).
 *
 * Pure presentation + client cart state — ZERO booking logic.
 */

export function ShoppingCart({
  timezone,
  currency = "USD",
  onReview,
}: {
  timezone: string;
  /** ISO-4217 currency for the est. total (per-line currency lives on each item). */
  currency?: string;
  onReview: () => void;
}) {
  const isNarrow = useIsNarrow();
  return isNarrow ? (
    <MobileCartBar
      timezone={timezone}
      currency={currency}
      onReview={onReview}
    />
  ) : (
    <DesktopCartRail
      timezone={timezone}
      currency={currency}
      onReview={onReview}
    />
  );
}

/* ============================================================================
   DESKTOP — the static right-rail "YOUR CART" panel (unchanged; never blocks the
   grid because it lives in its own layout column).
   ========================================================================== */

interface DayGroup {
  dateKey: string;
  label: string;
  items: CartSlot[];
  total: number;
}

/** Group cart items by their venue-local calendar date, ordered chronologically. */
function groupByDay(items: CartSlot[], timezone: string): DayGroup[] {
  const groups = new Map<string, CartSlot[]>();
  for (const it of items) {
    const key = venueDateKey(it.start_time, timezone);
    const bucket = groups.get(key);
    if (bucket) bucket.push(it);
    else groups.set(key, [it]);
  }
  return [...groups.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([dateKey, dayItems]) => ({
      dateKey,
      label: dateLabel(dateKey),
      items: dayItems,
      total: dayItems.reduce((s, it) => s + (it.estimate ?? 0), 0),
    }));
}

/** The venue-local YYYY-MM-DD of an ISO start, for day grouping. */
function venueDateKey(iso: string, timezone: string): string {
  try {
    return new Intl.DateTimeFormat("en-CA", {
      timeZone: timezone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(new Date(iso));
  } catch {
    return iso.slice(0, 10);
  }
}

function DesktopCartRail({
  timezone,
  currency,
  onReview,
}: {
  timezone: string;
  currency: string;
  onReview: () => void;
}) {
  const { items, count, totalEstimate, remove, clear } = useCart();

  if (count === 0) {
    return (
      <Box>
        <Paper
          component="aside"
          variant="outlined"
          aria-label="Your cart"
          sx={{ overflow: "hidden" }}
        >
          <Typography component="h2" variant="h6" sx={{ px: 2, py: 1.5 }}>
            Your cart
          </Typography>
          <Divider />
          <Typography color="text.secondary" sx={{ p: 2 }}>
            Cart is empty. Tap open hours on the sheet to add courts — mix
            courts and days, then review them together.
          </Typography>
        </Paper>
      </Box>
    );
  }

  const days = groupByDay(items, timezone);
  const slotWord = count === 1 ? "slot" : "slots";
  const hourWord = count === 1 ? "hr" : "hrs";

  return (
    <Box>
      <Paper
        component="aside"
        variant="outlined"
        aria-label="Your cart"
        sx={{ overflow: "hidden" }}
      >
        <Stack
          direction="row"
          spacing={1}
          sx={{ px: 2, py: 1.5, alignItems: "center" }}
        >
          <Typography component="h2" variant="h6" sx={{ flex: 1 }}>
            Your cart
          </Typography>
          <Chip label={`${count} ${slotWord}`} size="small" color="primary" />
          <Button
            type="button"
            variant="text"
            size="small"
            onClick={clear}
            aria-label={`Clear all ${count} ${slotWord} from the cart`}
          >
            Clear all
          </Button>
        </Stack>
        <Divider />

        <Box sx={{ maxHeight: "min(46dvh, 420px)", overflowY: "auto" }}>
          {days.map((day) => (
            <Box key={day.dateKey} sx={{ px: 2, py: 1.5 }}>
              <Stack
                direction="row"
                spacing={2}
                sx={{ justifyContent: "space-between" }}
              >
                <Typography variant="caption" color="text.secondary">
                  {day.label}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatMoney(day.total, currency)} est
                </Typography>
              </Stack>
              {day.items.map((item) => (
                <CartItemRow
                  key={`${item.court_id}|${item.start_time}`}
                  item={item}
                  timezone={timezone}
                  onRemove={() =>
                    remove({
                      court_id: item.court_id,
                      start_time: item.start_time,
                    })
                  }
                />
              ))}
              <Divider sx={{ mx: -2, mt: 0.5 }} />
            </Box>
          ))}
        </Box>

        <Stack spacing={1.5} sx={{ p: 2, pt: 1.5 }}>
          <Stack
            direction="row"
            spacing={2}
            sx={{ alignItems: "flex-end", justifyContent: "space-between" }}
          >
            <Box>
              <Typography color="text.secondary">Est. total</Typography>
              <Typography variant="caption" color="text.secondary">
                {count} {hourWord}
              </Typography>
            </Box>
            <Typography
              variant="h4"
              color="primary.main"
              sx={{ fontWeight: 800, fontVariantNumeric: "tabular-nums" }}
            >
              {formatMoney(totalEstimate, currency)}
            </Typography>
          </Stack>
          <Button
            type="button"
            variant="contained"
            fullWidth
            onClick={onReview}
          >
            Review &amp; pay
          </Button>
          <Typography variant="caption" color="text.secondary">
            Estimate only — the final price is shown at checkout before anything
            is charged.
          </Typography>
        </Stack>
      </Paper>
    </Box>
  );
}

/* ============================================================================
   MOBILE — ultra-minimal fixed bottom bar + opt-in detail sheet.
   ========================================================================== */

function MobileCartBar({
  timezone,
  currency,
  onReview,
}: {
  timezone: string;
  currency: string;
  onReview: () => void;
}) {
  const { items, count, totalEstimate, remove } = useCart();
  const [open, setOpen] = useState(false);
  const toggleRef = useRef<HTMLButtonElement>(null);

  // Close the sheet AND restore focus to the summary toggle — the single dismiss
  // path shared by the scrim, the chevron, and Esc, so a keyboard user never
  // loses their place to <body> (WCAG 2.4.3).
  const close = useCallback(() => {
    setOpen(false);
    toggleRef.current?.focus();
  }, []);

  // Never leave an expanded sheet open once the cart empties (e.g. the last
  // slot is removed from within the sheet) — the whole bar unmounts anyway.
  useEffect(() => {
    if (count === 0 && open) setOpen(false);
  }, [count, open]);

  // Empty cart → render nothing (no fake empty bar occluding the grid).
  if (count === 0) return null;

  const slotWord = count === 1 ? "slot" : "slots";
  const total = formatMoney(totalEstimate, currency);

  return (
    <>
      <Paper
        elevation={8}
        data-testid="cart-minibar"
        sx={{
          position: "fixed",
          zIndex: (theme) => theme.zIndex.appBar + 1,
          left: 12,
          right: 12,
          bottom: "calc(64px + env(safe-area-inset-bottom, 0px) + 8px)",
          minHeight: 56,
          px: 1,
          display: "flex",
          alignItems: "center",
          gap: 1,
          borderRadius: 999,
          border: 1,
          borderColor: "divider",
        }}
      >
        <ButtonBase
          type="button"
          ref={toggleRef}
          aria-expanded={open}
          aria-controls="cart-detail-sheet"
          aria-label={`${open ? "Hide" : "Show"} cart — ${count} ${slotWord} selected, estimated total ${total}`}
          onClick={() => setOpen((o) => !o)}
          sx={{
            flex: 1,
            minWidth: 0,
            minHeight: 44,
            px: 1,
            gap: 0.75,
            justifyContent: "flex-start",
            borderRadius: 999,
          }}
        >
          <Box
            aria-hidden="true"
            sx={{
              width: 8,
              height: 8,
              flexShrink: 0,
              borderRadius: "50%",
              bgcolor: "primary.main",
            }}
          />
          <Typography component="span" sx={{ fontWeight: 700 }} noWrap>
            {count} selected
          </Typography>
          <Typography
            component="span"
            color="text.secondary"
            aria-hidden="true"
          >
            ·
          </Typography>
          <Typography
            component="span"
            color="primary.main"
            sx={{ fontWeight: 800, fontVariantNumeric: "tabular-nums" }}
          >
            {total}
          </Typography>
          {open ? (
            <KeyboardArrowDownRounded aria-hidden="true" sx={{ ml: "auto" }} />
          ) : (
            <KeyboardArrowUpRounded aria-hidden="true" sx={{ ml: "auto" }} />
          )}
        </ButtonBase>
        <Button
          type="button"
          variant="contained"
          size="small"
          onClick={onReview}
          sx={{ flexShrink: 0, borderRadius: 999 }}
        >
          Review &amp; pay
        </Button>
      </Paper>

      <Drawer
        anchor="bottom"
        open={open}
        onClose={close}
        slotProps={{
          backdrop: {
            "data-testid": "cart-sheet-scrim",
          } as Record<string, string>,
          paper: {
            sx: {
              maxHeight: "80dvh",
              borderTopLeftRadius: 20,
              borderTopRightRadius: 20,
              pb: "env(safe-area-inset-bottom, 0px)",
            },
          },
        }}
      >
        <Box
          component="aside"
          id="cart-detail-sheet"
          aria-label="Your cart"
          sx={{ display: "flex", flexDirection: "column", minHeight: 0 }}
        >
          <Stack
            direction="row"
            spacing={1}
            sx={{ px: 2, py: 1.5, alignItems: "center" }}
          >
            <Typography component="h2" variant="h6" sx={{ flex: 1 }}>
              Your cart · {count} {slotWord}
            </Typography>
            <IconButton
              type="button"
              onClick={close}
              aria-label="Hide cart details"
            >
              <KeyboardArrowDownRounded aria-hidden="true" />
            </IconButton>
          </Stack>
          <Divider />

          <Box sx={{ flex: 1, overflowY: "auto", px: 2 }}>
            {items.map((item) => (
              <CartItemRow
                key={`${item.court_id}|${item.start_time}`}
                item={item}
                timezone={timezone}
                onRemove={() =>
                  remove({
                    court_id: item.court_id,
                    start_time: item.start_time,
                  })
                }
              />
            ))}
          </Box>

          <Divider />
          <Stack spacing={1.5} sx={{ p: 2 }}>
            <Stack
              direction="row"
              spacing={2}
              sx={{ alignItems: "baseline", justifyContent: "space-between" }}
            >
              <Typography color="text.secondary">Est. total</Typography>
              <Typography
                variant="h5"
                color="primary.main"
                sx={{ fontWeight: 800, fontVariantNumeric: "tabular-nums" }}
              >
                {total}
              </Typography>
            </Stack>
            <Button
              type="button"
              variant="contained"
              fullWidth
              onClick={onReview}
            >
              Review &amp; pay
            </Button>
            <Typography variant="caption" color="text.secondary">
              Estimate only — the final price is shown at checkout before
              anything is charged.
            </Typography>
          </Stack>
        </Box>
      </Drawer>
    </>
  );
}
